<template>
  <v-card width="100%" class="fill-height pa-4 main-card">
    <v-row v-if="loading">
      <v-col lg="6">
        <v-skeleton-loader v-for="skeletonIndex in 3" :key="skeletonIndex" type="card" />
      </v-col>
      <v-col lg="6">
        <v-skeleton-loader v-for="skeletonIndex in 6" :key="skeletonIndex" type="paragraph" />
      </v-col>
    </v-row>

    <v-row v-else>
      <v-col v-if="selectedCollaboratorProfile && isSkillsOutdated" cols="12" class="pb-0">
        <v-alert type="warning" variant="tonal" data-testid="skills-outdated-banner">
          <div class="d-flex align-center justify-space-between flex-wrap ga-2">
            <span>
              {{
                skillValuesAgeDays === null
                  ? t('collaboratorProfilesPage.skillsOutdatedBannerNever')
                  : t('collaboratorProfilesPage.skillsOutdatedBannerDays', { days: skillValuesAgeDays })
              }}
            </span>
            <v-btn size="small" variant="tonal" color="warning" @click="goToCollaboratorSkills">
              {{ t('collaboratorProfilesPage.openSkills') }}
            </v-btn>
          </div>
        </v-alert>
      </v-col>

      <v-col lg="4" cols="12">
        <v-card variant="flat">
          <v-row no-gutters class="pt-2">
            <v-col cols="12">
              <div class="d-flex">
                <collaborator-selector
                  :collaborator-profiles="collaboratorProfiles"
                  :selected-collaborator-uuid="selectedCollaboratorUuid ?? undefined"
                  @select-profile="selectCollaboratorProfile"
                />
                <div class="pt-2 pl-4">
                  <c-copy-button
                    v-if="selectedCollaboratorUuid"
                    data-testid="collaborator-profile-copy"
                    :get-data="getAllCopyData"
                  />
                </div>
              </div>
            </v-col>
          </v-row>

          <template v-if="selectedCollaboratorProfile">
            <SaveWrapper
              v-if="isSmallScreen"
              :save-in-progress="saveInProgress"
              :is-same="isSame"
              :save-collaborator-profile="saveCollaboratorProfile"
              :selected-collaborator-profile="selectedCollaboratorProfile"
              :updated-date="updatedDate"
              :t="t"
              :has-logged-user-permission="hasLoggedUserPermission"
            />

            <v-row>
              <v-col cols="12">
                <collaborator-base-info
                  :collaborator-profile="selectedCollaboratorProfile"
                  @update-profile="updateCollaboratorProfile"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-card width="100%" variant="tonal" data-testid="skills">
                  <v-row no-gutters>
                    <v-card-title class="w-100 d-flex justify-space-between">
                      {{ t('collaboratorProfilesPage.skills') }}
                      <c-copy-button data-testid="collaborator-profile-copy-skills" :get-data="getSkillsCopyData" />
                    </v-card-title>
                    <v-divider />

                    <v-card-text>
                      <v-row>
                        <collaborator-skills
                          :collaborator-skills="selectedCollaboratorProfile.collaborator.collaboratorSkillValues"
                        />
                      </v-row>
                    </v-card-text>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <collaborator-certifications
                  ref="collaboratorCertificationsRef"
                  :collaborator-profile="selectedCollaboratorProfile"
                  @update-profile="updateCollaboratorProfile"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12">
                <v-card width="100%" variant="tonal" data-testid="profile-url">
                  <v-row no-gutters>
                    <v-card-title class="justify-center">
                      {{ t('collaboratorProfilesPage.profileUrl') }}
                    </v-card-title>
                    <v-divider />

                    <v-card-text>
                      <v-row>
                        <v-text-field
                          v-model="selectedCollaboratorProfileUrl"
                          density="compact"
                          data-testid="profileUrlTextField"
                        />
                      </v-row>
                      <v-row>
                        <v-btn size="small" variant="outlined" class="my-2" @click="openProfileUrlInNewWindow">
                          {{ t('buttons.openInNewWindow') }}
                        </v-btn>
                      </v-row>
                    </v-card-text>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </v-card>
      </v-col>

      <v-col lg="8" cols="12">
        <v-card v-if="selectedCollaboratorProfile" variant="flat">
          <SaveWrapper
            v-if="!isSmallScreen"
            :save-in-progress="saveInProgress"
            :is-same="isSame"
            :save-collaborator-profile="saveCollaboratorProfile"
            :selected-collaborator-profile="selectedCollaboratorProfile"
            :updated-date="updatedDate"
            :has-logged-user-permission="hasLoggedUserPermission"
            class="pt-2"
          />

          <v-row class="other-info-row">
            <v-col cols="12">
              <collaborator-other-info
                :collaborator-profile="selectedCollaboratorProfile"
                @update-profile="updateCollaboratorProfile"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <collaborator-projects
                ref="collaboratorProjectsRef"
                :collaborator-profile="selectedCollaboratorProfile"
              />
            </v-col>
          </v-row>
        </v-card>
      </v-col>

      <v-col v-if="!selectedCollaboratorProfile" cols="12">
        <h3 class="text-center">{{ t('collaboratorProfilesPage.selectFirst') }}</h3>
      </v-col>
    </v-row>
  </v-card>
</template>

<script lang="ts" setup>
import { VBtn, VCol, VIcon, VRow } from 'vuetify/components';
import { useI18n } from 'vue-i18n';
import CollaboratorSelector from '@/components/CollaboratorProfilesComponents/CollaboratorSelector.vue';
import CollaboratorBaseInfo from '@/components/CollaboratorProfilesComponents/CollaboratorBaseInfo.vue';
import CollaboratorOtherInfo from '@/components/CollaboratorProfilesComponents/CollaboratorOtherInfo.vue';
import CollaboratorProjects from '@/components/CollaboratorProfilesComponents/CollaboratorProjects.vue';
import CollaboratorSkills from '@/components/CollaboratorComponents/CollaboratorSkills.vue';
import CollaboratorCertifications from '@/components/CollaboratorProfilesComponents/CollaboratorCertifications.vue';
import CCopyButton from '@/components/Common/CCopyButton.vue';
import { useCollaboratorProfilesPageLogic } from './useCollaboratorProfilesPage';

const {
  t,
  loading,
  collaboratorProfiles,
  selectedCollaboratorUuid,
  selectedCollaboratorProfile,
  saveInProgress,
  isSame,
  updatedDate,
  selectedCollaboratorProfileUrl,
  collaboratorProjectsRef,
  collaboratorCertificationsRef,
  isSmallScreen,
  hasLoggedUserPermission,
  selectCollaboratorProfile,
  updateCollaboratorProfile,
  saveCollaboratorProfile,
  openProfileUrlInNewWindow,
  getAllCopyData,
  getSkillsCopyData,
  skillValuesAgeDays,
  isSkillsOutdated,
  goToCollaboratorSkills,
} = useCollaboratorProfilesPageLogic();

const SaveWrapper = {
  components: {
    VBtn,
    VRow,
    VCol,
    VIcon,
  },
  template: `
    <v-row class="save-wrapper">
      <v-col cols="12" class="fill-height">
        <div class="d-flex align-center justify-end ga-1 fill-height" data-testid="save-metadata">
          {{ t('collaboratorProfilesPage.lastEdit') }} {{ updatedDate }}
          <template v-if="selectedCollaboratorProfile.updatedBy"
          >{{ t('collaboratorProfilesPage.by') }}
            {{ selectedCollaboratorProfile.updatedBy.fullName }}
          </template
          >

          <v-btn
            data-testid="save-button"
            :disabled="isSame || (!hasLoggedUserPermission('ERP_COLLABORATOR_PROFILES', ['UPDATE_ALL', 'UPDATE_OWN']))"
            :loading="saveInProgress"
            variant="flat"
            color="primary"
            @click="saveCollaboratorProfile"
          >
            {{ t('collaboratorProfilesPage.save') }}
          </v-btn>

          <icon v-if="!isSame" icon="ph:warning-duotone" width="24" height="24" style="color: rgb(var(--v-theme-warning-lighten-1, 255, 193, 7))" />
        </div>
      </v-col>
    </v-row>
  `,
  props: [
    'isSame',
    'saveInProgress',
    'saveCollaboratorProfile',
    'updatedDate',
    'selectedCollaboratorProfile',
    't',
    'hasLoggedUserPermission',
  ],
  setup: () => {
    const { t } = useI18n();

    return { t };
  },
};
</script>

<style lang="scss" scoped>
::v-deep(.ga-1) {
  gap: 1rem;
}

::v-deep(.save-wrapper) {
  height: 78px;
}

.other-info-row {
  margin-top: 35px;
}
</style>
